import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { canAnimate, clamp, ease, finePointer } from "./motion";

const HINTS = {
  fine: [
    "Hold anywhere to summon a worker.",
    "Grab a letter and throw it.",
    "Click the field to set a destination.",
  ],
  touch: [
    "Press and hold to summon a worker.",
    "Fling a letter.",
    "Tap the field to set a destination.",
  ],
};
const pad = (n) => String(n).padStart(2, "0");

function Wheel() {
  return (
    <svg
      className="bm-wheel"
      viewBox="0 0 20 20"
      width="16"
      height="16"
      aria-hidden="true"
    >
      <circle
        cx="10"
        cy="10"
        r="7.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <circle cx="10" cy="10" r="2.2" fill="currentColor" />
      <path
        d="M3 9.2h4.8M12.2 9.2H17M10 12.2v5.2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

// "Run your business on autopilot": the page drives itself through the story until you take control.
export default function Autopilot({ stops }) {
  const [enabled, setEnabled] = useState(false);
  const [hints, setHints] = useState(HINTS.fine);
  const [hint, setHint] = useState(0);
  const [trip, setTrip] = useState(null);
  const run = useRef(null);
  useEffect(() => {
    if (!canAnimate()) return;
    setEnabled(true);
    setHints(finePointer() ? HINTS.fine : HINTS.touch);
  }, []);
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = window.setInterval(() => setHint((i) => (i + 1) % 3), 4200);
    return () => window.clearInterval(timer);
  }, [enabled]);
  const disengage = useCallback(() => {
    run.current?.();
    run.current = null;
    setTrip(null);
  }, []);
  useEffect(() => disengage, [disengage]);
  const engage = () => {
    if (run.current) {
      disengage();
      return;
    }
    const root = document.documentElement;
    const behavior = root.style.scrollBehavior;
    let raf = 0;
    let timer = 0;
    let over = false;
    const interrupt = (e) => {
      if (
        e.type === "keydown" &&
        ["Shift", "Control", "Alt", "Meta"].includes(e.key)
      )
        return;
      if (e.target?.closest?.(".bm-autopilot-hud, .bm-autopilot-toggle"))
        return;
      disengage();
    };
    const events = ["wheel", "touchstart", "keydown", "pointerdown"];
    const finish = () => {
      over = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      root.style.scrollBehavior = behavior;
      root.classList.remove("is-autopilot");
      events.forEach((type) =>
        window.removeEventListener(type, interrupt, true),
      );
    };
    const visit = (i) => {
      if (over) return;
      if (i >= stops.length) {
        setTrip({
          index: stops.length - 1,
          label: "Destination reached",
          phase: "arrived",
        });
        timer = window.setTimeout(disengage, 2600);
        return;
      }
      const stop = stops[i];
      const node = document.querySelector(stop.selector);
      if (!node) {
        visit(i + 1);
        return;
      }
      const rect = node.getBoundingClientRect();
      const vh = window.innerHeight;
      const max = document.documentElement.scrollHeight - vh;
      const goal = stop.to
        ? stop.to(node)
        : stop.align === "center"
          ? rect.top + window.scrollY + rect.height / 2 - vh / 2
          : rect.top + window.scrollY - vh * (stop.align ?? 0.12);
      const to = clamp(goal, 0, max);
      const from = window.scrollY;
      const duration =
        stop.duration || clamp(Math.abs(to - from) * 1.1, 900, 2600);
      const start = performance.now();
      setTrip({
        index: i,
        label: stop.label,
        phase: "cruise",
        dwell: stop.dwell || 2800,
      });
      const tick = (t) => {
        if (over) return;
        const q = clamp((t - start) / duration);
        window.scrollTo(0, from + (to - from) * ease(q));
        if (q < 1) raf = requestAnimationFrame(tick);
        else {
          setTrip({
            index: i,
            label: stop.label,
            phase: "dwell",
            dwell: stop.dwell || 2800,
          });
          timer = window.setTimeout(() => visit(i + 1), stop.dwell || 2800);
        }
      };
      raf = requestAnimationFrame(tick);
    };
    root.style.scrollBehavior = "auto";
    root.classList.add("is-autopilot");
    events.forEach((type) =>
      window.addEventListener(type, interrupt, {
        capture: true,
        passive: true,
      }),
    );
    run.current = finish;
    setTrip({ index: 0, label: "Engaging autopilot", phase: "cruise" });
    window.dispatchEvent(new Event("bm-autopilot"));
    timer = window.setTimeout(() => visit(0), 650);
  };
  if (!enabled) return null;
  return (
    <>
      <div className="bm-hero-controls">
        <button
          type="button"
          className={"bm-autopilot-toggle" + (trip ? " is-on" : "")}
          aria-pressed={!!trip}
          onClick={engage}
        >
          <Wheel />
          {trip ? "Autopilot engaged" : "Engage autopilot"}
        </button>
        <span className="bm-hero-hint" key={hint} aria-hidden="true">
          {hints[hint]}
        </span>
      </div>
      {trip &&
        createPortal(
          <div className={"bm-autopilot-hud is-" + trip.phase} role="status">
            <span className="bm-hud-mode">
              <Wheel /> Autopilot
            </span>
            <span className="bm-hud-step">
              {pad(trip.index + 1)} / {pad(stops.length)}
            </span>
            <span className="bm-hud-label" key={trip.label}>
              {trip.label}
            </span>
            <button type="button" onClick={disengage}>
              Take control <kbd>esc</kbd>
            </button>
            <i
              key={trip.index + trip.phase}
              className="bm-hud-progress"
              style={{ "--dwell": (trip.dwell || 2600) + "ms" }}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
